import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SessionActions } from './session.actions';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { environment } from '@environment/environment';
import { RestService } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { ErrorActions } from '../error';
import { ACCOUNT_TYPE, PLAN_NAMES } from '@domain/constants/app-variables';
import { Router } from '@angular/router';
import { LANDING_PAGES } from '@domain/constants/landing-pages';
import { sessionStateKey } from '.';
import { Store } from '@ngrx/store';

@Injectable({
	providedIn: 'root',
})
export class SessionEffects {
	constructor(
		private actions$: Actions<any>,
		private restService: RestService,
		private urlService: UrlService,
		private router: Router,
		private store: Store
	) {}

	loginSubmitStarted$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.submit.start),
			switchMap(({ email, password, rememberMe }) =>
				this.restService
					.POST(this.urlService.loginUrl(), {
						email,
						password,
						rememberMe,
						type: environment.app,
					})
					.pipe(
						map((result) =>
							SessionActions.login.submit.success({ payload: { user: result.data } })
						),
						catchError((error) =>
							of(SessionActions.login.submit.failed({ payload: { message: error.error } }))
						)
					)
			)
		)
	);

	loginSubmitSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.submit.success),
			switchMap(({ payload }) =>
				of(SessionActions.login.setup.start({ payload: { ...payload, redirect: true } }))
			)
		)
	);

	loginSetupStarted$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.setup.start),
			switchMap(({ payload }) =>
				forkJoin([
					this.restService.GET(this.urlService.loginSetupUrl()),
					this.restService.GET(this.urlService.notificationSettings()),
				]).pipe(
					map(([loginSetup, notificationSettings]) =>
						SessionActions.login.setup.success({
							payload: {
								user: loginSetup.user,
								account: loginSetup.account,
								accountSettings: loginSetup.accountSettings,
								userSettings: loginSetup.userSettings,
								products: loginSetup.planProducts,
								notificationSettings: notificationSettings.data,
								redirect: payload.redirect,
								addOnSubscriptions: loginSetup.addOnSubscriptions,
								activeIntegrations: loginSetup.activeIntegrations,
							},
						})
					),
					catchError((error) => {
						if (error.status === 401) {
							localStorage.removeItem(sessionStateKey);
							return;
						}
						return of(SessionActions.login.setup.failed({ payload: { message: error.error } }));
					})
				)
			)
		)
	);

	loginSetupSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SessionActions.login.setup.success),
				filter(({ payload }) => payload.redirect),
				tap(async ({ payload }) => {
					const accountType = payload.account.accountType.toUpperCase();

					if (accountType === ACCOUNT_TYPE.BROKER || accountType === ACCOUNT_TYPE.SHIPPER) {
						const landingPage = payload.user.landingPage || LANDING_PAGES.DASHBOARD.value;

						await this.router.navigate([landingPage]);
						return;
					}

					if (
						accountType === ACCOUNT_TYPE.CARRIER &&
						[
							PLAN_NAMES.PRICING.toUpperCase(),
							PLAN_NAMES.CARRIER.toUpperCase(),
							PLAN_NAMES.CARRIER_PLUS_PRICING.toUpperCase(),
						].includes(payload.account.plan.toUpperCase())
					) {
						await this.router.navigate([LANDING_PAGES.DASHBOARD.value]);
						return;
					}

					await this.router.navigate(['admin/accounts']);
				})
			),
		{ dispatch: false }
	);

	loginSessionFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionActions.login.submit.failed, SessionActions.login.setup.failed),
			tap(() => localStorage.removeItem(sessionStateKey)),
			switchMap(({ payload }) => of(ErrorActions.error.show({ payload })))
		)
	);
}
